import { useEffect, type ComponentType } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router';
import { CalendarDays, History, LayoutGrid, Shield, Target, Users } from 'lucide-react';
import { useIsAdmin } from './core/auth/access';
import { startData, stopData } from './core/store';
import { StoreError } from './core/ui/Connection';
import { NoticeStack } from './core/ui/notices';
import { useData } from './core/store';
import { cn } from './core/ui/cn';
import { Presence } from './core/ui/Presence';
import { AccountMenu } from './core/ui/AccountMenu';
import { ProjectsScreen } from './sections/roadmap/projects/ProjectsScreen';
import { ReleaseScreen } from './sections/roadmap/release/ReleaseScreen';
import { PeopleScreen } from './sections/roadmap/people/PeopleScreen';
import { LaunchScreen } from './sections/launch/LaunchScreen';
import { HistoryScreen } from './sections/history/HistoryScreen';
import { AdminScreen } from './sections/admin/AdminScreen';
import { HotkeysButton } from './sections/roadmap/shared/Hotkeys';
import { DeployGuardDialog } from './sections/roadmap/shared/deployGuard';
import { ConfirmHost } from './core/ui/confirm';
import { TooltipProvider } from './core/ui/Tooltip';

// Реестр разделов: строка здесь = вкладка. Служебные (tool) — иконками у выхода. Как добавить раздел — CLAUDE.md.
type Section = { path: string; label: string; icon: ComponentType<{ className?: string }>; tool?: boolean; adminOnly?: boolean; right?: boolean };
const SECTIONS: Section[] = [
  { path: '/roadmap/projects', label: 'Projects View', icon: LayoutGrid },
  { path: '/roadmap/people', label: 'People View', icon: Users },
  { path: '/roadmap/release-plan', label: 'Release Plan', icon: CalendarDays },
  { path: '/launches', label: 'Launch Board', icon: Target, right: true }, // другая сущность — прижата к правому краю
  { path: '/history', label: 'History', icon: History, tool: true },
  { path: '/admin', label: 'Admin', icon: Shield, tool: true, adminOnly: true },
];

function Shell() {
  const isAdmin = useIsAdmin();
  const failed = useData((s) => s.status === 'error');
  const visible = SECTIONS.filter((s) => !s.adminOnly || isAdmin);
  return (
    <div className="h-screen flex flex-col p-4 bg-gray-50">
      {/* шапка одной строкой (Глеб, 2026-09-11): логотип и имя, вкладки, справа кто здесь, справка и аккаунт;
          вкладки стоят по нижнему краю и стыкуются с карточкой экрана */}
      <header className="flex items-end gap-6">
        <div className="flex items-center gap-3 self-center shrink-0 pb-1">
          <div className="w-9 h-9 rounded-[10px] bg-[#EDEBFC] grid place-items-center shrink-0">
            <img src="/octopi-logo.svg" alt="OctopiPoker" width={24} height={24} />
          </div>
          <h1 className="text-xl text-gray-800 whitespace-nowrap"><span className="font-bold">OctopiPoker</span> <span className="font-normal text-gray-500">Roadmap</span></h1>
        </div>
        {/* вкладки одной группой на сером фоне, как у браузера: правая («Launch Board») — тот же ряд, не отдельная кнопка */}
        <nav className="flex-1 min-w-0 flex gap-1 items-end bg-gray-100 rounded-t-xl px-2 pt-2" aria-label="Views">
          {visible.filter((s) => !s.tool).map((s) => (
            <NavLink
              key={s.path} to={s.path}
              className={({ isActive }) => cn(
                // все вкладки — «ярлыки» с рамкой без низа, стоят на карточке; активная белая и выше на пиксель, неактивные серые
                'min-h-0 px-4 py-2 text-sm font-medium rounded-t-lg border border-b-0 transition-colors flex items-center gap-2 whitespace-nowrap',
                s.right && 'ml-auto',
                isActive ? 'bg-white border-gray-300 text-blue-600 -mb-px pb-[9px]' : 'bg-gray-200/60 border-gray-300/60 text-gray-600 hover:bg-gray-200 hover:text-gray-800',
              )}
            >
              <s.icon className="w-4 h-4" />
              {s.label}
            </NavLink>
          ))}
        </nav>
        {/* справка отдельно; кто ещё здесь и я — одна группа (история и админка — в меню аккаунта) */}
        <div className="flex items-center gap-3 self-center shrink-0 pb-1 pl-3">
          <HotkeysButton />
          <span className="w-px h-6 bg-gray-300" aria-hidden />
          <span className="flex items-center gap-2">
            <Presence />
            <AccountMenu tools={visible.filter((s) => s.tool)} />
          </span>
        </div>
      </header>
      <main className="flex-1 min-h-0 flex flex-col">
        {failed ? <StoreError /> : <Routes>
          <Route path="/" element={<Navigate to="/roadmap/projects" replace />} />
          <Route path="/roadmap/projects" element={<ProjectsScreen />} />
          <Route path="/roadmap/people" element={<PeopleScreen />} />
          <Route path="/roadmap/release-plan" element={<ReleaseScreen />} />
          <Route path="/launches" element={<LaunchScreen />} />
          <Route path="/history" element={<HistoryScreen />} />
          <Route path="/admin" element={<AdminScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>}
        {/* окно сторожа даты выхода — одно на приложение: спрашивалка в deployGuard модульная */}
        <DeployGuardDialog />
        <ConfirmHost />
      </main>
      <NoticeStack />
    </div>
  );
}

export function App() {
  // Ядро стартует, когда профиль прочитан (AuthGate уже пропустил), и гасится при выходе.
  useEffect(() => {
    void startData();
    return () => stopData();
  }, []);
  return (
    <BrowserRouter>
      <TooltipProvider><Shell /></TooltipProvider>
    </BrowserRouter>
  );
}
